import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "./store/store";
import { checkAuth, setAuthChecked } from "./store/slices/authSlice";

// Guest Pages
import Home from "./pages/Guest/Home";
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import VerifyEmail from "./pages/Auth/VerifyEmail";
import ResetPassword from "./pages/Auth/ResetPassword";

// User Pages
import DashboardHome from "./pages/Dashboard/Home";
import Wallet from "./pages/Dashboard/Wallet";
import TradingBots from "./pages/Dashboard/TradingBots";
import Airdrops from "./pages/Dashboard/Airdrops";
import Profile from "./pages/Dashboard/Profile";
import KycVerification from "./pages/Dashboard/KycVerification";
import Referral from "./pages/Dashboard/Referral";

// Admin Pages
import AdminDashboard from "./pages/Admin/Dashboard";
import AdminTransactions from "./pages/Admin/Transactions";
import AdminKyc from "./pages/Admin/Kyc";
import AdminSettings from "./pages/Admin/Settings";
import AdminUsers from "./pages/Admin/AdminUsers";
import AdminTradingBots from "./pages/Admin/TradingBotsManagement";
import AdminReferral from "./pages/Admin/ReferralManagement";
import AdminSupportManagement from "./pages/Admin/SupportManagement";
import UserSupportPage from "./pages/Dashboard/Support";
import globalStyle from "./styles/globalStyle";


// Support
import SupportLiveAlert from "./components/Support/SupportLiveAlert";

// ─── Loading Screen ──────────────────────────────────────────────────────────

const LoadingScreen: React.FC = () => (
  <div className="w-full h-screen bg-mobile md:bg-desktop bg-no-repeat bg-cover bg-center bg-fixed">
    <div className="h-full flex flex-col gap-2 items-center justify-center bg-black/80">
      <svg
        className={`${globalStyle.loadingSpinner} mx-auto`}
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <div className="text-center">
        <p className="mt-4 text-gray-400">Loading...</p>
      </div>
    </div>
  </div>
);

// ─── Route Guards ─────────────────────────────────────────────────────────────

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requireAdmin?: boolean;
}> = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, user, isLoading, authChecked } = useSelector(
    (state: RootState) => state.auth,
  );

  if (!authChecked || isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (requireAdmin && user?.role !== "admin")
    return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

const VerifiedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isLoading, authChecked } = useSelector(
    (state: RootState) => state.auth,
  );

  if (!authChecked || isLoading) return <LoadingScreen />;
  if (user && !user.email_verified)
    return <Navigate to="/verify-email" replace />;

  return <>{children}</>;
};

const KycVerifiedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoading, authChecked } = useSelector(
    (state: RootState) => state.auth,
  );

  if (!authChecked || isLoading) return <LoadingScreen />;

  return <>{children}</>;
};

const GuestRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, authChecked } = useSelector(
    (state: RootState) => state.auth,
  );

  if (!authChecked || isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

// ─── App Content ──────────────────────────────────────────────────────────────

const AppContent: React.FC = () => {
  const { isLoading, authChecked, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );

  if (!authChecked || isLoading) return <LoadingScreen />;

  return (
    <>

      {/* All page routes */}
      <Routes>
        {/* ── Guest-only routes ── */}
        <Route
          path="/"
          element={
            <GuestRoute>
              <Home />
            </GuestRoute>
          }
        />
        <Route
          path="/login"
          element={
            <GuestRoute>
              <Login />
            </GuestRoute>
          }
        />
        <Route
          path="/register"
          element={
            <GuestRoute>
              <Register />
            </GuestRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestRoute>
              <ForgotPassword />
            </GuestRoute>
          }
        />
        <Route
          path="/verify-email"
          element={
            <GuestRoute>
              <VerifyEmail />
            </GuestRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <GuestRoute>
              <ResetPassword />
            </GuestRoute>
          }
        />

        {/* ── Authenticated user routes ── */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <VerifiedRoute>
                <DashboardHome />
              </VerifiedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <VerifiedRoute>
                <KycVerifiedRoute>
                  <Wallet />
                </KycVerifiedRoute>
              </VerifiedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/trading-bots"
          element={
            <ProtectedRoute>
              <VerifiedRoute>
                <KycVerifiedRoute>
                  <TradingBots />
                </KycVerifiedRoute>
              </VerifiedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/airdrops"
          element={
            <ProtectedRoute>
              <VerifiedRoute>
                <KycVerifiedRoute>
                  <Airdrops />
                </KycVerifiedRoute>
              </VerifiedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/referral"
          element={
            <ProtectedRoute>
              <VerifiedRoute>
                <Referral />
              </VerifiedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kyc"
          element={
            <ProtectedRoute>
              <KycVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <VerifiedRoute>
                <UserSupportPage />
              </VerifiedRoute>
            </ProtectedRoute>
          }
        />

        {/* ── Admin routes ── */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requireAdmin>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/trading-bots"
          element={
            <ProtectedRoute requireAdmin>
              <AdminTradingBots />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/transactions"
          element={
            <ProtectedRoute requireAdmin>
              <AdminTransactions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/kyc"
          element={
            <ProtectedRoute requireAdmin>
              <AdminKyc />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requireAdmin>
              <AdminSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/referral"
          element={
            <ProtectedRoute requireAdmin>
              <AdminReferral />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/support"
          element={
            <ProtectedRoute requireAdmin>
              <AdminSupportManagement />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/*
        ── Support widgets ──────────────────────────────────────
        MUST be OUTSIDE <Routes> — they are not Route components.
        They render as floating overlays on top of every page.
      */}
      {isAuthenticated && <SupportLiveAlert />}
    </>
  );
};

// ─── Root App ─────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(setAuthChecked());
    }, 10000);

    dispatch(checkAuth()).finally(() => {
      clearTimeout(timeoutId);
    });

    return () => clearTimeout(timeoutId);
  }, [dispatch]);

  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
